'use client';
/** Shared guards for rendering plugin-contributed UI: a plugin component is third-party code, so one
 *  broken render must stay INSIDE its slot (a /p/<plugin> page, a Settings section), never take down
 *  the shell around it. Used by the plugin host route and the Settings deck's plugin sections. */
import { Component as ReactComponent, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

/** Class component — error boundaries have no hook equivalent. By default a crash is replaced by a notice
 *  frame in the slot. A slot that cannot show one (an overlay host mounted beside the shell) passes `fallback`
 *  to draw something else, `null` for nothing, and `onFailure` to hand the crash to the surface that can. */
export class PluginErrorBoundary extends ReactComponent<{ children: ReactNode; notice: string; fallback?: ReactNode; onFailure?: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } { return { failed: true }; }
  componentDidCatch() { this.props.onFailure?.(); }
  render() {
    if (this.state.failed) return this.props.fallback !== undefined ? this.props.fallback : <PluginPlaceholder text={this.props.notice} />;
    return this.props.children;
  }
}

/** A single host-owned notice frame. Optional actions stay inside the frame rather than wrapping it. */
export function PluginPlaceholder({ text, action, role }: { text: string; action?: ReactNode; role?: 'alert' }) {
  return (
    <div role={role} className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-6 text-meta text-muted-foreground">
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 break-words">{text}</span>
      {action}
    </div>
  );
}
